import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventType, InteractionStatus, AuthenticationResult, EventMessage, AuthError } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@lp/environment';
import { APP_ROUTES } from '@lp/routes';
import { hasRolesClaims, Role } from '@lp/models/auth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _router = inject(Router);
  private _msalService = inject(MsalService);
  private _broadcast = inject(MsalBroadcastService);

  public loggedIn = signal(false);
  public roles: Role[] = [];
  public userId: string | undefined;

  /** --- PUBLIC API --- */

  public login(): void {
    this._router.navigateByUrl(APP_ROUTES.loader).then(() => {
      this._msalService.loginRedirect({
        scopes: environment.msal.apiScopes
      });
    });
  }

  public logout(): void {
    this._router.navigateByUrl(APP_ROUTES.loader).then(() => {
      this._msalService.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}`,
        account: this._msalService.instance.getActiveAccount()
      });
    });
  }

  public init(destroyRef: DestroyRef): void {
    const instance = this._msalService.instance;

    this._msalService
      .handleRedirectObservable()
      .pipe(
        takeUntilDestroyed(destroyRef),
        tap((result) => {
          if (result) {
            instance.setActiveAccount(result.account);
          }
        }),
        catchError((err) => {
          console.warn('[AUTH] Redirect handling failed', err);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this._finalizeInit();
      });

    instance.enableAccountStorageEvents();
    this._subscribeToMsalEvents(destroyRef);
    this._subscribeToInteractionStatus(destroyRef);
  }

  private _finalizeInit(): void {
    this._ensureActiveAccount();
    this._setLoggedIn();
    this._loadRolesFromActiveAccount();
  }

  private _ensureActiveAccount(): void {
    const instance = this._msalService.instance;
    if (!instance.getActiveAccount() && instance.getAllAccounts().length > 0) {
      instance.setActiveAccount(instance.getAllAccounts()[0]);
    }
  }

  private _setLoggedIn(): void {
    const accounts = this._msalService.instance.getAllAccounts();
    this.loggedIn.set(accounts.length > 0 && accounts.some((acc) => acc.idTokenClaims));
  }

  private _loadRolesFromActiveAccount(): void {
    const account = this._msalService.instance.getActiveAccount();
    const claims = account?.idTokenClaims;

    if (account && hasRolesClaims(claims)) {
      this.roles = claims.roles ?? [];
      this.userId = claims.oid;
    } else {
      this.roles = [];
      this.userId = undefined;
    }
  }

  private _subscribeToMsalEvents(destroyRef: DestroyRef): void {
    this._broadcast.msalSubject$
      .pipe(
        takeUntilDestroyed(destroyRef),
        tap((msg: EventMessage) => {
          if (msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED) {
            if (this._msalService.instance.getAllAccounts().length === 0) {
              window.location.pathname = '/';
            }
          }

          if (msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            const payload = msg.payload as AuthenticationResult;
            if (payload?.account) {
              this._msalService.instance.setActiveAccount(payload.account);
            }
          }
          if (msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            this.roles = [];
            this.userId = undefined;
            if (this.loggedIn()) {
              this.loggedIn.set(false);
            }
            this._router.parseUrl(APP_ROUTES.error);
            return;
          }
          this._loadRolesFromActiveAccount();
          this._setLoggedIn();
        })
      )
      .subscribe();
  }

  private _subscribeToInteractionStatus(destroyRef: DestroyRef): void {
    this._broadcast.inProgress$
      .pipe(
        takeUntilDestroyed(destroyRef),
        filter((status) => status === InteractionStatus.None),
        tap(() => {
          this._ensureActiveAccount();
          this._setLoggedIn();
        }),
        switchMap(() => {
          const account = this._msalService.instance.getActiveAccount();

          if (!account) {
            this.roles = [];
            if (this.userId) {
              this.userId = undefined;
            }
            if (this.loggedIn()) {
              this.loggedIn.set(false);
            }
            return EMPTY;
          }
          if (!account.idTokenClaims) {
            console.warn(
              `[AUTH] InteractionStatus.None: Active account found, but idTokenClaims are initially missing. Attempting silent token acquisition to refresh.`
            );
          }

          return this._msalService
            .acquireTokenSilent({
              account,
              scopes: environment.msal.apiScopes
            })
            .pipe(
              tap((result: AuthenticationResult) => {
                this._msalService.instance.setActiveAccount(result.account);
                if (result.account && result.account.idTokenClaims && hasRolesClaims(result.account.idTokenClaims)) {
                  this._loadRolesFromActiveAccount();
                  if (!this.loggedIn()) {
                    this.loggedIn.set(true);
                  }
                } else {
                  console.warn(
                    '[AUTH] Silent token acquisition attempt completed (did not require interaction), but idTokenClaims are STILL missing in the result. Session is incomplete. Forcing re-login.'
                  );
                  this.roles = [];
                  this.userId = undefined;
                  if (this.loggedIn()) {
                    this.loggedIn.set(false);
                  }
                  this.login();
                }
              }),
              catchError((err: AuthError) => {
                console.warn(`[AUTH] Silent token acquisition failed. Error Code: ${err.errorCode}`, err);
                this.roles = [];
                this.userId = undefined;
                if (this.loggedIn()) {
                  this.loggedIn.set(false);
                }
                console.warn(
                  `[AUTH] Forcing re-login due to silent token acquisition failure (Error: ${err.errorCode}).`
                );
                this.login();
                return EMPTY;
              })
            );
        })
      )
      .subscribe();
  }
}
